import React, { useState, useEffect, useCallback } from 'react';
import {
  MessageSquare, CheckCircle2, AlertTriangle, Copy,
  ExternalLink, RefreshCw, Loader2, Zap, Globe, Key,
  Phone, Clock, BarChart3, Wifi, WifiOff,
  Shield, Activity, FileText
} from 'lucide-react';

interface WhatsAppStatus {
  provider: string;
  connected: boolean;
  phoneNumber: string;
  instanceName: string;
  apiUrl: string;
  webhookUrl: string;
  messagesToday: number;
  demandsToday: number;
  totalMessages: number;
  lastMessageAt: string | null;
  lastMessageSender: string | null;
  lastMessagePreview: string | null;
}

const API_URL = import.meta.env.VITE_API_URL || '/api';

const fetchStatus = async (): Promise<WhatsAppStatus> => {
  const token = localStorage.getItem('gab_token');
  const r = await fetch(`${API_URL}/integrations/whatsapp/status`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  if (!r.ok) throw new Error('Falha ao buscar status');
  return r.json();
};

const EVOLUTION_DOCS_URL = 'https://doc.evolution-api.com';

// ─────────────────────────────────────────────────────────────

const CopyButton: React.FC<{ value: string }> = ({ value }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold bg-slate-100 dark:bg-slate-800 hover:bg-blue-100 dark:hover:bg-blue-950/40 text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 border border-slate-200/60 dark:border-slate-700/60 rounded-lg transition-all"
    >
      {copied ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copiado!' : 'Copiar'}
    </button>
  );
};

// ─────────────────────────────────────────────────────────────

export const WhatsAppIntegration: React.FC = () => {
  const [status, setStatus] = useState<WhatsAppStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchStatus();
      setStatus(data);
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar status da integração');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR') + ' às ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const providerLabel = (p: string) => {
    if (p === 'evolution') return 'Evolution API';
    if (p === 'meta') return 'Meta Cloud API';
    return 'Nenhum provedor configurado';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 flex-col gap-3">
        <AlertTriangle className="w-8 h-8 text-amber-500" />
        <p className="text-slate-500 text-sm">{error}</p>
        <button onClick={load} className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold">
          Tentar novamente
        </button>
      </div>
    );
  }

  const s = status!;
  const isConnected = s.connected;
  const isMock = s.provider === 'mock' || !s.provider;

  return (
    <div className="space-y-6 text-left max-w-4xl">

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-3">
            <div className="p-2 bg-emerald-500/10 rounded-xl">
              <MessageSquare className="w-5 h-5 text-emerald-600" />
            </div>
            WhatsApp Business
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Integração com Evolution API para recebimento automatizado de demandas
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:bg-slate-200 dark:hover:bg-slate-800 text-xs font-semibold rounded-xl transition-all text-slate-600 dark:text-slate-400"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Atualizar Status
        </button>
      </div>

      {/* Card de Status Principal */}
      <div className={`glass-panel p-6 rounded-3xl border-2 ${
        isConnected
          ? 'border-emerald-400/50 bg-emerald-50/30 dark:bg-emerald-950/10'
          : isMock
          ? 'border-amber-300/50 bg-amber-50/20 dark:bg-amber-950/10'
          : 'border-red-300/50 bg-red-50/20 dark:bg-red-950/10'
      }`}>
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-center gap-4">
            {/* Ícone de status */}
            <div className={`p-4 rounded-2xl ${
              isConnected ? 'bg-emerald-100 dark:bg-emerald-950/50'
              : isMock ? 'bg-amber-100 dark:bg-amber-950/50'
              : 'bg-red-100 dark:bg-red-950/50'
            }`}>
              {isConnected
                ? <Wifi className="w-8 h-8 text-emerald-600" />
                : isMock
                ? <AlertTriangle className="w-8 h-8 text-amber-500" />
                : <WifiOff className="w-8 h-8 text-red-500" />
              }
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className={`text-lg font-black ${
                  isConnected ? 'text-emerald-700 dark:text-emerald-400'
                  : isMock ? 'text-amber-700 dark:text-amber-400'
                  : 'text-red-700 dark:text-red-400'
                }`}>
                  {isConnected ? '● CONECTADO' : isMock ? '◐ MODO DESENVOLVIMENTO' : '○ DESCONECTADO'}
                </span>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-300 font-semibold mt-0.5">
                {providerLabel(s.provider)}
              </p>
              {isMock && !isConnected && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  Configure as variáveis de ambiente para ativar a integração real.
                </p>
              )}
            </div>
          </div>

          {/* Detalhes da conexão */}
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-xs">
            <div>
              <span className="text-slate-400 text-[10px] uppercase tracking-wider font-semibold block">Provedor</span>
              <span className="font-bold text-slate-700 dark:text-slate-200">{providerLabel(s.provider)}</span>
            </div>
            <div>
              <span className="text-slate-400 text-[10px] uppercase tracking-wider font-semibold block">Número</span>
              <span className="font-bold text-slate-700 dark:text-slate-200 font-mono">
                {s.phoneNumber || '—'}
              </span>
            </div>
            <div>
              <span className="text-slate-400 text-[10px] uppercase tracking-wider font-semibold block">Instância</span>
              <span className="font-bold text-slate-700 dark:text-slate-200 font-mono">
                {s.instanceName || '—'}
              </span>
            </div>
            <div>
              <span className="text-slate-400 text-[10px] uppercase tracking-wider font-semibold block">Última mensagem</span>
              <span className="font-bold text-slate-700 dark:text-slate-200">{formatDate(s.lastMessageAt)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Grid de Métricas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          {
            label: 'Mensagens Hoje',
            value: s.messagesToday,
            icon: <MessageSquare className="w-4 h-4 text-blue-500" />,
            color: 'text-blue-700 dark:text-blue-400',
            bg: 'bg-blue-50 dark:bg-blue-950/40'
          },
          {
            label: 'Demandas Hoje',
            value: s.demandsToday,
            icon: <FileText className="w-4 h-4 text-emerald-500" />,
            color: 'text-emerald-700 dark:text-emerald-400',
            bg: 'bg-emerald-50 dark:bg-emerald-950/40'
          },
          {
            label: 'Total Recebidas',
            value: s.totalMessages,
            icon: <BarChart3 className="w-4 h-4 text-indigo-500" />,
            color: 'text-indigo-700 dark:text-indigo-400',
            bg: 'bg-indigo-50 dark:bg-indigo-950/40'
          },
          {
            label: 'Status da API',
            value: isConnected ? 'Online' : isMock ? 'Dev' : 'Offline',
            icon: <Activity className="w-4 h-4 text-slate-500" />,
            color: isConnected ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400',
            bg: 'bg-slate-50 dark:bg-slate-900'
          },
        ].map(m => (
          <div key={m.label} className="glass-panel p-4 rounded-2xl border border-slate-200/70 dark:border-slate-800/70">
            <div className={`p-2 rounded-xl ${m.bg} w-fit mb-2`}>{m.icon}</div>
            <div className={`text-2xl font-black tabular-nums ${m.color}`}>{m.value}</div>
            <div className="text-[10px] text-slate-500 font-medium">{m.label}</div>
          </div>
        ))}
      </div>

      {/* URL do Webhook */}
      <div className="glass-panel p-6 rounded-3xl border border-slate-200 dark:border-slate-800 space-y-4">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-blue-500" />
          <h2 className="font-bold text-sm">URL do Webhook</h2>
          <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 text-[10px] font-bold rounded border border-blue-200/50">
            CONFIGURE NA EVOLUTION API
          </span>
        </div>
        <p className="text-xs text-slate-500">
          Configure esta URL como webhook na sua instância Evolution API. Todas as mensagens recebidas serão processadas automaticamente pela IA.
        </p>

        <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl">
          <code className="text-xs font-mono text-slate-700 dark:text-slate-300 flex-1 break-all">
            {s.webhookUrl}
          </code>
          <CopyButton value={s.webhookUrl} />
        </div>

        {s.apiUrl && (
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-400">API URL configurada:</span>
            <code className="text-[10px] font-mono text-slate-500">{s.apiUrl}</code>
          </div>
        )}
      </div>

      {/* Configuração das Variáveis de Ambiente */}
      <div className="glass-panel p-6 rounded-3xl border border-slate-200 dark:border-slate-800 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Key className="w-4 h-4 text-amber-500" />
            <h2 className="font-bold text-sm">Configuração do Servidor</h2>
          </div>
          {!isConnected && (
            <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 text-[10px] font-bold rounded border border-amber-200/50">
              AÇÃO NECESSÁRIA
            </span>
          )}
        </div>

        <p className="text-xs text-slate-500">
          Configure as seguintes variáveis de ambiente no arquivo <code className="font-mono bg-slate-100 dark:bg-slate-800 px-1 rounded">.env</code> do servidor backend e reinicie o serviço:
        </p>

        <div className="space-y-2">
          {[
            { key: 'WHATSAPP_PROVIDER', value: 'evolution', desc: 'Provedor de integração (evolution | meta)' },
            { key: 'WHATSAPP_API_URL', value: 'https://sua-instancia-evolution.com', desc: 'URL base da sua instância Evolution API' },
            { key: 'WHATSAPP_API_TOKEN', value: 'seu_token_aqui', desc: 'Token de autenticação da Evolution API' },
            { key: 'WHATSAPP_INSTANCE_NAME', value: 'gabinete_dani_capitao', desc: 'Nome da instância WhatsApp conectada' },
            { key: 'WHATSAPP_PHONE_NUMBER', value: '+5514999999999', desc: 'Número WhatsApp Business conectado' },
            { key: 'BACKEND_URL', value: 'https://api.seugabinete.com.br', desc: 'URL pública do seu backend (para o webhook URL)' },
          ].map(env => (
            <div key={env.key} className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-900/60 border border-slate-200/60 dark:border-slate-800/60 rounded-xl">
              <code className="text-[11px] font-mono font-bold text-indigo-600 dark:text-indigo-400 shrink-0 pt-0.5">{env.key}</code>
              <div className="flex-1 min-w-0">
                <code className="text-[10px] font-mono text-slate-400 block">="{env.value}"</code>
                <span className="text-[10px] text-slate-400">{env.desc}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Guia Evolution API */}
      <div className="glass-panel p-6 rounded-3xl border border-slate-200 dark:border-slate-800 space-y-5">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-emerald-500" />
          <h2 className="font-bold text-sm">Como conectar a Evolution API</h2>
        </div>

        <div className="space-y-3">
          {[
            {
              step: '1',
              title: 'Instale a Evolution API',
              desc: 'Faça o deploy da Evolution API via Docker ou no seu servidor VPS.',
              code: 'docker pull atendai/evolution-api:latest'
            },
            {
              step: '2',
              title: 'Crie uma instância WhatsApp',
              desc: 'No painel da Evolution API, crie uma instância com o nome definido em WHATSAPP_INSTANCE_NAME.',
              code: null
            },
            {
              step: '3',
              title: 'Configure o Webhook',
              desc: 'Na instância criada, configure o webhook apontando para a URL exibida acima.',
              code: null
            },
            {
              step: '4',
              title: 'Conecte o número WhatsApp',
              desc: 'Escaneie o QR Code gerado pela Evolution API com o WhatsApp Business do gabinete.',
              code: null
            },
            {
              step: '5',
              title: 'Configure as variáveis e reinicie',
              desc: 'Preencha todas as variáveis de ambiente acima e reinicie o servidor backend.',
              code: null
            },
          ].map(item => (
            <div key={item.step} className="flex gap-4">
              <div className="w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-black flex items-center justify-center shrink-0 mt-0.5">
                {item.step}
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-slate-700 dark:text-slate-200">{item.title}</p>
                <p className="text-xs text-slate-500 mt-0.5">{item.desc}</p>
                {item.code && (
                  <div className="flex items-center gap-2 mt-2 p-2.5 bg-slate-900 dark:bg-black rounded-xl">
                    <code className="text-xs font-mono text-emerald-400 flex-1">{item.code}</code>
                    <CopyButton value={item.code} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <a
          href={EVOLUTION_DOCS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline mt-2"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Documentação completa da Evolution API
        </a>
      </div>

      {/* Última Mensagem Recebida */}
      {s.lastMessageAt && (
        <div className="glass-panel p-5 rounded-2xl border border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-slate-400" />
            <h3 className="font-bold text-sm">Última Mensagem Recebida</h3>
          </div>
          <div className="flex items-start gap-4 text-xs">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Phone className="w-3 h-3 text-slate-400" />
                <span className="font-mono font-bold text-slate-700 dark:text-slate-300">{s.lastMessageSender}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-3 h-3 text-slate-400" />
                <span className="text-slate-500">{formatDate(s.lastMessageAt)}</span>
              </div>
            </div>
            {s.lastMessagePreview && (
              <div className="flex-1 p-3 bg-slate-50 dark:bg-slate-900 rounded-xl text-slate-600 dark:text-slate-300 italic leading-relaxed">
                "{s.lastMessagePreview}{s.lastMessagePreview.length >= 80 ? '...' : ''}"
              </div>
            )}
          </div>
        </div>
      )}

      {/* Segurança */}
      <div className="flex items-start gap-3 p-4 bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200/40 dark:border-blue-900/30 rounded-2xl">
        <Shield className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
        <div className="text-xs text-blue-700 dark:text-blue-300">
          <strong>Segurança:</strong> O webhook é protegido pela variável{' '}
          <code className="font-mono bg-blue-100 dark:bg-blue-950/50 px-1 rounded">WHATSAPP_WEBHOOK_SECRET</code>.
          Configure um valor aleatório forte e informe o mesmo na Evolution API.
          Todos os arquivos recebidos são armazenados com autenticação obrigatória.
        </div>
      </div>
    </div>
  );
};

export default WhatsAppIntegration;
